/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  ChangeRequest,
  DeleteAdminV1StoresStoreIdQcTemplatesIdPathParameters,
  DeleteAdminV1StoresStoreIdWorkflowTemplatesIdPathParameters,
  DeletePushDevicePathParameters,
  GetAdminV1MeNotificationsParams,
  GetAdminV1StoresStoreIdOrdersIdWorkflowPathParameters,
  GetAdminV1StoresStoreIdProduction200,
  GetAdminV1StoresStoreIdProductionParams,
  GetAdminV1StoresStoreIdProductionPathParameters,
  GetAdminV1StoresStoreIdQcTemplates200,
  GetAdminV1StoresStoreIdQcTemplatesIdPathParameters,
  GetAdminV1StoresStoreIdQcTemplatesPathParameters,
  GetAdminV1StoresStoreIdWorkflowTemplates200,
  GetAdminV1StoresStoreIdWorkflowTemplatesIdPathParameters,
  GetAdminV1StoresStoreIdWorkflowTemplatesParams,
  GetAdminV1StoresStoreIdWorkflowTemplatesPathParameters,
  OrderWorkflow,
  PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyBody,
  PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyPathParameters,
  PatchAdminV1StoresStoreIdQcTemplatesIdBody,
  PatchAdminV1StoresStoreIdQcTemplatesIdPathParameters,
  PatchAdminV1StoresStoreIdWorkflowTemplatesIdBody,
  PatchAdminV1StoresStoreIdWorkflowTemplatesIdPathParameters,
  PostAdminV1MeNotificationsIdReadPathParameters,
  PostAdminV1MeNotificationsReadAll200,
  PostAdminV1StoresStoreIdOrdersIdChangeRequestsBody,
  PostAdminV1StoresStoreIdOrdersIdChangeRequestsPathParameters,
  PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveBody,
  PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolvePathParameters,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompletePathParameters,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyEnablePathParameters,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsPathParameters,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcPathParameters,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendPathParameters,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipBody,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipPathParameters,
  PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyStartPathParameters,
  PostAdminV1StoresStoreIdQcTemplatesBody,
  PostAdminV1StoresStoreIdQcTemplatesPathParameters,
  PostAdminV1StoresStoreIdWorkflowTemplatesBody,
  PostAdminV1StoresStoreIdWorkflowTemplatesIdDuplicateBody,
  PostAdminV1StoresStoreIdWorkflowTemplatesIdDuplicatePathParameters,
  PostAdminV1StoresStoreIdWorkflowTemplatesIdMakeDefaultPathParameters,
  PostAdminV1StoresStoreIdWorkflowTemplatesPathParameters,
  PushDevice,
  PushSettings,
  PushTestResult,
  PutAdminV1StoresStoreIdOrdersIdWorkflowBody,
  PutAdminV1StoresStoreIdOrdersIdWorkflowPathParameters,
  QcTemplate,
  RegisterPushDeviceBody,
  StaffNotification,
  StaffNotificationPage,
  WorkflowTemplate
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Bounded (20 per store), returned whole with settings.read. ?summary=true returns id, name and isDefault only and needs orders.write instead
 * @summary Workflow templates of the store
 */
export const getAdminV1StoresStoreIdWorkflowTemplates = (
    { storeId }: GetAdminV1StoresStoreIdWorkflowTemplatesPathParameters,
    params?: GetAdminV1StoresStoreIdWorkflowTemplatesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdWorkflowTemplates200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdWorkflowTemplates200>(
      {url: `/admin/v1/stores/${storeId}/workflow-templates`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Stage and sub-step keys are unique within the template; QC stages need a checklist of the same store; alert recipients must be active members. The first template of a store becomes its default
 * @summary Create a workflow template
 */
export const postAdminV1StoresStoreIdWorkflowTemplates = (
    { storeId }: PostAdminV1StoresStoreIdWorkflowTemplatesPathParameters,
    postAdminV1StoresStoreIdWorkflowTemplatesBody: BodyType<PostAdminV1StoresStoreIdWorkflowTemplatesBody>,
 options?: SecondParameter<typeof customInstance<WorkflowTemplate>>,) => {
      return customInstance<WorkflowTemplate>(
      {url: `/admin/v1/stores/${storeId}/workflow-templates`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdWorkflowTemplatesBody
    },
      options);
    }
  /**
 * @summary One workflow template
 */
export const getAdminV1StoresStoreIdWorkflowTemplatesId = (
    { storeId, id }: GetAdminV1StoresStoreIdWorkflowTemplatesIdPathParameters,
 options?: SecondParameter<typeof customInstance<WorkflowTemplate>>,) => {
      return customInstance<WorkflowTemplate>(
      {url: `/admin/v1/stores/${storeId}/workflow-templates/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Orders already running keep the snapshot they started with
 * @summary Rename or replace the stages
 */
export const patchAdminV1StoresStoreIdWorkflowTemplatesId = (
    { storeId, id }: PatchAdminV1StoresStoreIdWorkflowTemplatesIdPathParameters,
    patchAdminV1StoresStoreIdWorkflowTemplatesIdBody: BodyType<PatchAdminV1StoresStoreIdWorkflowTemplatesIdBody>,
 options?: SecondParameter<typeof customInstance<WorkflowTemplate>>,) => {
      return customInstance<WorkflowTemplate>(
      {url: `/admin/v1/stores/${storeId}/workflow-templates/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdWorkflowTemplatesIdBody
    },
      options);
    }
  /**
 * 409 RESOURCE_IN_USE for the default while other templates exist. Orders keep their snapshot
 * @summary Delete a workflow template
 */
export const deleteAdminV1StoresStoreIdWorkflowTemplatesId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdWorkflowTemplatesIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/workflow-templates/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Newly paid orders get the default; orders in progress keep theirs
 * @summary Make this the store's default template
 */
export const postAdminV1StoresStoreIdWorkflowTemplatesIdMakeDefault = (
    { storeId, id }: PostAdminV1StoresStoreIdWorkflowTemplatesIdMakeDefaultPathParameters,
 options?: SecondParameter<typeof customInstance<WorkflowTemplate>>,) => {
      return customInstance<WorkflowTemplate>(
      {url: `/admin/v1/stores/${storeId}/workflow-templates/${id}/make-default`, method: 'POST'
    },
      options);
    }
  /**
 * Copying to another store copies the referenced QC checklists too (reusing a same-named one there)
 * @summary Copy a template, into this store or another store of the organization
 */
export const postAdminV1StoresStoreIdWorkflowTemplatesIdDuplicate = (
    { storeId, id }: PostAdminV1StoresStoreIdWorkflowTemplatesIdDuplicatePathParameters,
    postAdminV1StoresStoreIdWorkflowTemplatesIdDuplicateBody: BodyType<PostAdminV1StoresStoreIdWorkflowTemplatesIdDuplicateBody>,
 options?: SecondParameter<typeof customInstance<WorkflowTemplate>>,) => {
      return customInstance<WorkflowTemplate>(
      {url: `/admin/v1/stores/${storeId}/workflow-templates/${id}/duplicate`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdWorkflowTemplatesIdDuplicateBody
    },
      options);
    }
  /**
 * Bounded (20 per store), returned whole
 * @summary QC checklists of the store
 */
export const getAdminV1StoresStoreIdQcTemplates = (
    { storeId }: GetAdminV1StoresStoreIdQcTemplatesPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdQcTemplates200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdQcTemplates200>(
      {url: `/admin/v1/stores/${storeId}/qc-templates`, method: 'GET'
    },
      options);
    }
  /**
 * Up to 60 fields: yes/no, choice (with Other), text, number, file. Field keys are unique; choice fields need options
 * @summary Create a QC checklist
 */
export const postAdminV1StoresStoreIdQcTemplates = (
    { storeId }: PostAdminV1StoresStoreIdQcTemplatesPathParameters,
    postAdminV1StoresStoreIdQcTemplatesBody: BodyType<PostAdminV1StoresStoreIdQcTemplatesBody>,
 options?: SecondParameter<typeof customInstance<QcTemplate>>,) => {
      return customInstance<QcTemplate>(
      {url: `/admin/v1/stores/${storeId}/qc-templates`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdQcTemplatesBody
    },
      options);
    }
  /**
 * @summary One QC checklist
 */
export const getAdminV1StoresStoreIdQcTemplatesId = (
    { storeId, id }: GetAdminV1StoresStoreIdQcTemplatesIdPathParameters,
 options?: SecondParameter<typeof customInstance<QcTemplate>>,) => {
      return customInstance<QcTemplate>(
      {url: `/admin/v1/stores/${storeId}/qc-templates/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Workflows already running keep the checklist they started with
 * @summary Rename or replace the fields
 */
export const patchAdminV1StoresStoreIdQcTemplatesId = (
    { storeId, id }: PatchAdminV1StoresStoreIdQcTemplatesIdPathParameters,
    patchAdminV1StoresStoreIdQcTemplatesIdBody: BodyType<PatchAdminV1StoresStoreIdQcTemplatesIdBody>,
 options?: SecondParameter<typeof customInstance<QcTemplate>>,) => {
      return customInstance<QcTemplate>(
      {url: `/admin/v1/stores/${storeId}/qc-templates/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdQcTemplatesIdBody
    },
      options);
    }
  /**
 * 409 RESOURCE_IN_USE while a workflow template's QC stage uses it
 * @summary Delete a QC checklist
 */
export const deleteAdminV1StoresStoreIdQcTemplatesId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdQcTemplatesIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/qc-templates/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Newest workflow first, with the current stage, its due time and the order's labels. Filter by current stage key, label, or order number / email
 * @summary Production board: orders with an active workflow
 */
export const getAdminV1StoresStoreIdProduction = (
    { storeId }: GetAdminV1StoresStoreIdProductionPathParameters,
    params?: GetAdminV1StoresStoreIdProductionParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdProduction200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdProduction200>(
      {url: `/admin/v1/stores/${storeId}/production`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Stages with their runs (status, due, delay, notes, files, QC submissions, parcels, review attempts), labels and change requests. 404 before the order is fully paid
 * @summary The order's production workflow
 */
export const getAdminV1StoresStoreIdOrdersIdWorkflow = (
    { storeId, id }: GetAdminV1StoresStoreIdOrdersIdWorkflowPathParameters,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow`, method: 'GET'
    },
      options);
    }
  /**
 * Completed stages whose key exists in the new template are kept; the new template resumes at its first stage without a completed counterpart. Attaches a workflow to a paid order that has none
 * @summary Switch the order to another template
 */
export const putAdminV1StoresStoreIdOrdersIdWorkflow = (
    { storeId, id }: PutAdminV1StoresStoreIdOrdersIdWorkflowPathParameters,
    putAdminV1StoresStoreIdOrdersIdWorkflowBody: BodyType<PutAdminV1StoresStoreIdOrdersIdWorkflowBody>,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdOrdersIdWorkflowBody
    },
      options);
    }
  /**
 * Stages run in order: a top-level stage starts only when nothing before it is open; a sub-step only while its stage runs. 409 WORKFLOW_STAGE_NOT_ACTIVE otherwise
 * @summary Start a pending stage
 */
export const postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyStart = (
    { storeId, id, key }: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyStartPathParameters,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow/stages/${key}/start`, method: 'POST'
    },
      options);
    }
  /**
 * A delayed stage needs delayReason (422 DELAY_REASON_REQUIRED). Customer stages take an outcome when the customer answered another way. Completing a stage with sub-steps closes the open ones. The delivered stage marks shipped fulfillments delivered
 * @summary Mark a stage done
 */
export const postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyComplete = (
    { storeId, id, key }: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompletePathParameters,
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteBody: BodyType<PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteBody>,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow/stages/${key}/complete`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteBody
    },
      options);
    }
  /**
 * @summary Skip a stage with a reason
 */
export const postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkip = (
    { storeId, id, key }: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipPathParameters,
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipBody: BodyType<PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipBody>,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow/stages/${key}/skip`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipBody
    },
      options);
    }
  /**
 * Only before the workflow has moved past it; a sub-step of a running stage starts at once
 * @summary Switch on an optional stage (CAD)
 */
export const postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyEnable = (
    { storeId, id, key }: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyEnablePathParameters,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow/stages/${key}/enable`, method: 'POST'
    },
      options);
    }
  /**
 * Notes also land on the order timeline (staff only)
 * @summary Set an exact due time, add a note or attach files
 */
export const patchAdminV1StoresStoreIdOrdersIdWorkflowStagesKey = (
    { storeId, id, key }: PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyPathParameters,
    patchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyBody: BodyType<PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyBody>,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow/stages/${key}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyBody
    },
      options);
    }
  /**
 * Answers are recorded, a No does not block. File answers are images or videos of 20 MB at most. A running QC stage completes; a completed one takes a new submission after rework without moving the workflow
 * @summary Submit the QC checklist
 */
export const postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQc = (
    { storeId, id, key }: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcPathParameters,
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcBody: BodyType<PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcBody>,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow/stages/${key}/qc`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcBody
    },
      options);
    }
  /**
 * send_files: emails the files (PDFs attached, others linked) and completes the stage. customer_review: emails a signed review link (a new attempt; older links answer 404) and waits for the customer's answer
 * @summary Send files to the customer
 */
export const postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySend = (
    { storeId, id, key }: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendPathParameters,
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendBody: BodyType<PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendBody>,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow/stages/${key}/send`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendBody
    },
      options);
    }
  /**
 * The parcel carrying the items ships every open line as one fulfillment (stock sold). The stage completes and emails the tracking links once every parcel is tracked
 * @summary Record carrier and tracking for parcels
 */
export const postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcels = (
    { storeId, id, key }: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsPathParameters,
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsBody: BodyType<PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsBody>,
 options?: SecondParameter<typeof customInstance<OrderWorkflow>>,) => {
      return customInstance<OrderWorkflow>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/workflow/stages/${key}/parcels`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsBody
    },
      options);
    }
  /**
 * extraCost is recorded only, never charged. Open change requests label the order changes_requested
 * @summary Record a change the customer asked for
 */
export const postAdminV1StoresStoreIdOrdersIdChangeRequests = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdChangeRequestsPathParameters,
    postAdminV1StoresStoreIdOrdersIdChangeRequestsBody: BodyType<PostAdminV1StoresStoreIdOrdersIdChangeRequestsBody>,
 options?: SecondParameter<typeof customInstance<ChangeRequest>>,) => {
      return customInstance<ChangeRequest>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/change-requests`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdChangeRequestsBody
    },
      options);
    }
  /**
 * @summary Resolve a change request with a note
 */
export const postAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolve = (
    { storeId, id, requestId }: PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolvePathParameters,
    postAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveBody: BodyType<PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveBody>,
 options?: SecondParameter<typeof customInstance<ChangeRequest>>,) => {
      return customInstance<ChangeRequest>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/change-requests/${requestId}/resolve`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveBody
    },
      options);
    }
  /**
 * Newest first, with the unread count for the bell
 * @summary In-app notifications of the signed-in member
 */
export const getAdminV1MeNotifications = (
    params?: GetAdminV1MeNotificationsParams,
 options?: SecondParameter<typeof customInstance<StaffNotificationPage>>,) => {
      return customInstance<StaffNotificationPage>(
      {url: `/admin/v1/me/notifications`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Mark one notification read
 */
export const postAdminV1MeNotificationsIdRead = (
    { id }: PostAdminV1MeNotificationsIdReadPathParameters,
 options?: SecondParameter<typeof customInstance<StaffNotification>>,) => {
      return customInstance<StaffNotification>(
      {url: `/admin/v1/me/notifications/${id}/read`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Mark every notification read
 */
export const postAdminV1MeNotificationsReadAll = (

 options?: SecondParameter<typeof customInstance<PostAdminV1MeNotificationsReadAll200>>,) => {
      return customInstance<PostAdminV1MeNotificationsReadAll200>(
      {url: `/admin/v1/me/notifications/read-all`, method: 'POST'
    },
      options);
    }
  /**
 * The VAPID key to subscribe with and the member's registered devices
 * @summary Web push settings of the signed-in member
 */
export const getPushSettings = (

 options?: SecondParameter<typeof customInstance<PushSettings>>,) => {
      return customInstance<PushSettings>(
      {url: `/admin/v1/me/push`, method: 'GET'
    },
      options);
    }
  /**
 * Idempotent on the endpoint: registering it again refreshes the keys, and signing in as another member on the same browser moves it. A member keeps the ten most recent devices
 * @summary Register this browser for push
 */
export const registerPushDevice = (
    registerPushDeviceBody: BodyType<RegisterPushDeviceBody>,
 options?: SecondParameter<typeof customInstance<PushDevice>>,) => {
      return customInstance<PushDevice>(
      {url: `/admin/v1/me/push/devices`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: registerPushDeviceBody
    },
      options);
    }
  /**
 * @summary Stop push to one device
 */
export const deletePushDevice = (
    { id }: DeletePushDevicePathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/me/push/devices/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Send a test push to every device of the member
 */
export const sendTestPush = (

 options?: SecondParameter<typeof customInstance<PushTestResult>>,) => {
      return customInstance<PushTestResult>(
      {url: `/admin/v1/me/push/test`, method: 'POST'
    },
      options);
    }
  export type GetAdminV1StoresStoreIdWorkflowTemplatesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdWorkflowTemplates>>>
export type PostAdminV1StoresStoreIdWorkflowTemplatesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdWorkflowTemplates>>>
export type GetAdminV1StoresStoreIdWorkflowTemplatesIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdWorkflowTemplatesId>>>
export type PatchAdminV1StoresStoreIdWorkflowTemplatesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdWorkflowTemplatesId>>>
export type DeleteAdminV1StoresStoreIdWorkflowTemplatesIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdWorkflowTemplatesId>>>
export type PostAdminV1StoresStoreIdWorkflowTemplatesIdMakeDefaultResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdWorkflowTemplatesIdMakeDefault>>>
export type PostAdminV1StoresStoreIdWorkflowTemplatesIdDuplicateResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdWorkflowTemplatesIdDuplicate>>>
export type GetAdminV1StoresStoreIdQcTemplatesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdQcTemplates>>>
export type PostAdminV1StoresStoreIdQcTemplatesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdQcTemplates>>>
export type GetAdminV1StoresStoreIdQcTemplatesIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdQcTemplatesId>>>
export type PatchAdminV1StoresStoreIdQcTemplatesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdQcTemplatesId>>>
export type DeleteAdminV1StoresStoreIdQcTemplatesIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdQcTemplatesId>>>
export type GetAdminV1StoresStoreIdProductionResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdProduction>>>
export type GetAdminV1StoresStoreIdOrdersIdWorkflowResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdOrdersIdWorkflow>>>
export type PutAdminV1StoresStoreIdOrdersIdWorkflowResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdOrdersIdWorkflow>>>
export type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyStartResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyStart>>>
export type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyComplete>>>
export type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkip>>>
export type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyEnableResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyEnable>>>
export type PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdOrdersIdWorkflowStagesKey>>>
export type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQc>>>
export type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySend>>>
export type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcels>>>
export type PostAdminV1StoresStoreIdOrdersIdChangeRequestsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdChangeRequests>>>
export type PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolve>>>
export type GetAdminV1MeNotificationsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1MeNotifications>>>
export type PostAdminV1MeNotificationsIdReadResult = NonNullable<Awaited<ReturnType<typeof postAdminV1MeNotificationsIdRead>>>
export type PostAdminV1MeNotificationsReadAllResult = NonNullable<Awaited<ReturnType<typeof postAdminV1MeNotificationsReadAll>>>
export type GetPushSettingsResult = NonNullable<Awaited<ReturnType<typeof getPushSettings>>>
export type RegisterPushDeviceResult = NonNullable<Awaited<ReturnType<typeof registerPushDevice>>>
export type DeletePushDeviceResult = NonNullable<Awaited<ReturnType<typeof deletePushDevice>>>
export type SendTestPushResult = NonNullable<Awaited<ReturnType<typeof sendTestPush>>>
